import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const nameOf = (m) => m?.display_name || m?.hostname || "";

const Shell = ({ title, open, onClose, children, footer, wide }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className={wide ? "max-w-3xl" : "max-w-lg"}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      {children}
      {footer && <DialogFooter>{footer}</DialogFooter>}
    </DialogContent>
  </Dialog>
);

export const Output = ({ r }) => (
  <div className="rounded-md bg-slate-950 text-slate-100 text-xs font-mono p-3 max-h-72 overflow-auto whitespace-pre-wrap" data-testid="command-output">
    <div className={r.ok ? "text-emerald-400" : "text-amber-400"}>{r.name} — {r.code === null || r.code === undefined ? "sans réponse" : `code ${r.code}`}</div>
    {r.output || "(aucune sortie)"}
  </div>
);

export const MessageDialog = ({ m, onClose }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/machines/${m.machine_id}/message`, { text }); toast.success(`Message affiché sur ${nameOf(m)}`); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title={`Message pour ${nameOf(m)}`} open={!!m} onClose={onClose}
      footer={<Button onClick={send} disabled={!text.trim() || busy} data-testid="message-send-btn">Envoyer</Button>}>
      <p className="text-sm text-slate-500">La fenêtre de discussion s'ouvre sur le poste avec votre message. L'utilisateur peut répondre.</p>
      <Textarea autoFocus rows={4} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Bonjour, je vais intervenir sur votre poste dans 5 minutes." data-testid="message-text-input" />
    </Shell>
  );
};

export const CommandDialog = ({ m, onClose }) => {
  const [command, setCommand] = useState("");
  const [shell, setShell] = useState("powershell");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const win = m?.os === "Windows";
  const run = async () => {
    setBusy(true); setRes(null);
    try { setRes((await api.post(`/machines/${m.machine_id}/command`, { command, shell: win ? shell : "auto" }, { timeout: 140000 })).data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell wide title={`Exécuter une commande sur ${nameOf(m)}`} open={!!m} onClose={onClose}
      footer={<Button onClick={run} disabled={!command.trim() || busy} data-testid="command-run-btn">{busy ? "Exécution…" : "Exécuter"}</Button>}>
      <div className="flex items-center gap-3 text-sm">
        {win ? ["powershell", "cmd"].map((s) => (
          <label key={s} className="flex items-center gap-1.5"><input type="radio" checked={shell === s} onChange={() => setShell(s)} data-testid={`command-shell-${s}`} />{s === "cmd" ? "Invite de commandes" : "PowerShell"}</label>
        )) : <span className="text-slate-500">Terminal (bash)</span>}
        <span className="text-slate-400 ml-auto">Droits administrateur · 2 min maximum</span>
      </div>
      <Textarea autoFocus rows={3} className="font-mono text-sm" value={command} onChange={(e) => setCommand(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) run(); }} placeholder="Get-Service | Where Status -eq Running" data-testid="command-input" />
      {res && <Output r={res} />}
    </Shell>
  );
};

export const NoteDialog = ({ m, onClose, onSaved }) => {
  const [notes, setNotes] = useState("");
  useEffect(() => setNotes(m?.notes || ""), [m]);
  const save = async () => {
    try { await api.patch(`/machines/${m.machine_id}`, { notes }); toast.success("Note enregistrée"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell title={`Note sur ${nameOf(m)}`} open={!!m} onClose={onClose} footer={<Button onClick={save} data-testid="note-save-btn">Enregistrer</Button>}>
      <p className="text-sm text-slate-500">Visible seulement par les techniciens, dans l'onglet Notes du poste.</p>
      <Textarea autoFocus rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex. : imprimante partagée sur ce poste, mot de passe BIOS chez le client…" data-testid="note-input" />
    </Shell>
  );
};
